/** Shop settings: built-in default -> organization -> location (docs/shop/PRD.md §8.9). No money here. */
export interface ShopSettingsValues {
  laborRounding?: { mode: 'nearest' | 'up'; incrementMinutes: number; minimumMinutes: number };
  approvalSender?: { smsFrom?: string; emailFrom?: string };
  warrantyManufacturers?: string[];
}

export interface ResolvedShopSettings {
  laborRounding: { mode: 'nearest' | 'up'; incrementMinutes: number; minimumMinutes: number };
  approvalSender: { smsFrom: string | null; emailFrom: string | null };
  warrantyManufacturers: string[];
}

// Review round 2, 2026-09-19: `resolveShopSettings` used to be fed only values that had just
// passed `validateSettings` on write (which requires mode/incrementMinutes/minimumMinutes
// together -- laborRounding was never a partial write). #4939 now feeds this function values READ
// STRAIGHT FROM FIRESTORE (org and location settings docs), so that write-time guarantee no
// longer protects it: a hand-edited doc, a pre-this-field legacy doc, or any future write path
// that doesn't go through validateSettings can hand this function a PARTIAL laborRounding object.
// Without this guard, `{ ...layer.laborRounding }` would whole-replace `acc.laborRounding` with
// that partial object -- recurring defect shape #1 (full-replace writers must not drop siblings)
// showing up on the READ side instead of the write side. A malformed layer is now IGNORED (the
// accumulator, i.e. the prior layer's or the built-in default, is kept) rather than trusted.
function isCompleteLaborRounding(value: unknown): value is ResolvedShopSettings['laborRounding'] {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (v.mode === 'nearest' || v.mode === 'up')
    && typeof v.incrementMinutes === 'number'
    && typeof v.minimumMinutes === 'number';
}

export function resolveShopSettings(org?: ShopSettingsValues | null, location?: ShopSettingsValues | null): ResolvedShopSettings {
  const start: ResolvedShopSettings = {
    laborRounding: { mode: 'nearest', incrementMinutes: 15, minimumMinutes: 15 },
    approvalSender: { smsFrom: null, emailFrom: null },
    warrantyManufacturers: [],
  };
  return [org ?? {}, location ?? {}].reduce<ResolvedShopSettings>((acc, layer) => ({
    laborRounding: isCompleteLaborRounding(layer.laborRounding) ? { ...layer.laborRounding } : acc.laborRounding,
    approvalSender: {
      smsFrom: layer.approvalSender?.smsFrom ?? acc.approvalSender.smsFrom,
      emailFrom: layer.approvalSender?.emailFrom ?? acc.approvalSender.emailFrom,
    },
    warrantyManufacturers: layer.warrantyManufacturers ? [...layer.warrantyManufacturers] : acc.warrantyManufacturers,
  }), start);
}
